import React from 'react';
import {
  InboxOutlined as InboxOutlinedIcon,
  MessageOutlined as MessageOutlinedIcon,
  WhatsApp as WhatsAppIcon,
  Mic as MicIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { formatDistanceToNowStrict } from 'date-fns';

const CHANNELS = [
  { key: 'all', label: 'All', icon: <InboxOutlinedIcon fontSize="inherit" /> },
  { key: 'web', label: 'Web', icon: <MessageOutlinedIcon fontSize="inherit" /> },
  { key: 'whatsapp', label: 'WhatsApp', icon: <WhatsAppIcon fontSize="inherit" /> },
  { key: 'voice', label: 'Voice', icon: <MicIcon fontSize="inherit" /> },
];

const DEFAULT_STATUS = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'resolved', label: 'Resolved' },
];

const STATUS_BY_MODE = {
  bot: [
    { key: 'all', label: 'All' },
    { key: 'open', label: 'Active' },
    { key: 'resolved', label: 'Resolved' },
  ],
  ticket: [
    { key: 'all', label: 'All' },
    { key: 'waiting', label: 'Waiting' },
    { key: 'assigned', label: 'Assigned' },
    { key: 'resolved', label: 'Resolved' },
  ],
  none: [],
};

/** Purpose: Build a short avatar label from a display name. Why: Threads without profile images still need an identity. */
const initials = (value = 'V') =>
  String(value || 'V')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
    .slice(0, 2) || 'V';

const channelTone = {
  web: 'bg-sky-500/10 text-sky-700 dark:text-sky-200',
  whatsapp: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-200',
  voice: 'bg-violet-500/10 text-violet-700 dark:text-violet-200',
};

/** Purpose: Map legacy pending/closed values to current statuses. Why: Old and new records must use the same inbox filters. */
const normalizeStatus = (status) => {
  const normalized = String(status || 'open').trim().toLowerCase();
  if (normalized === 'pending') return 'assigned';
  if (normalized === 'closed') return 'resolved';
  return normalized;
};
/** Purpose: Resolve the assignee label from a string or populated user. Why: API responses may contain either representation. */
const assignedName = (assignedTo) => {
  if (!assignedTo) return '';
  if (typeof assignedTo === 'string') return assignedTo;
  return assignedTo.name || assignedTo.email || '';
};

/** Purpose: render filterable conversation rows for the active inbox category. Why: list selection and status cues must stay consistent while the thread is open. */
export default function InboxConversationList({
  conversations = [],
  filters = {},
  onFilterChange,
  onChatViewChange,
  activeConversationId,
  loading = false,
  onSelectConversation,
  pagination = { page: 1, pages: 1 },
  onLoadMore,
  conversationKind,
  statusMode = 'conversation',
}) {
  const statuses = STATUS_BY_MODE[statusMode] || DEFAULT_STATUS;
  return (
    <aside className="flex h-full min-h-0 w-full flex-col rounded-2xl border border-slate-200/80 bg-white/90 p-3 shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/80">
      <div className="space-y-2.5">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-3 top-3.5 text-slate-400" fontSize="small" />
          <input
            value={filters.search || ''}
            onChange={(event) => onFilterChange?.({ search: event.target.value })}
            placeholder="Search conversations"
            className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2.5 pl-10 pr-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-cyan-400/60 focus:bg-white focus:ring-2 focus:ring-cyan-500/15 dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:bg-slate-900"
          />
        </div>

        <div className="flex flex-wrap gap-1.5">
          {statuses.map((status) => {
            const active = (filters.status || 'all') === status.key;
            return (
              <button
                key={status.key}
                type="button"
                onClick={() => { onChatViewChange?.('all'); onFilterChange?.({ status: status.key }); }}
                className={[
                  'rounded-full px-2.5 py-1.5 text-xs font-medium transition',
                  active
                    ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-950'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10',
                ].join(' ')}
              >
                {status.label}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {CHANNELS.map((channel) => {
            const active = (filters.channel || 'all') === channel.key;
            return (
              <button
                key={channel.key}
                type="button"
                onClick={() => onFilterChange?.({ channel: channel.key })}
                className={[
                  'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium transition',
                  active
                    ? 'bg-cyan-500/15 text-cyan-700 dark:bg-cyan-400/15 dark:text-cyan-200'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10',
                ].join(' ')}
              >
                {channel.icon}
                {channel.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="animate-pulse rounded-3xl border border-slate-200/80 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/5">
                <div className="h-4 w-28 rounded bg-slate-200 dark:bg-white/10" />
                <div className="mt-3 h-3 w-full rounded bg-slate-200 dark:bg-white/10" />
                <div className="mt-2 h-3 w-3/4 rounded bg-slate-200 dark:bg-white/10" />
              </div>
            ))}
          </div>
        ) : conversations.length ? (
          <div className="space-y-2">
            {conversations.map((conversation) => {
              const isActive = conversation._id === activeConversationId;
              const channelClass = channelTone[conversation.channel] || channelTone.web;
              const lastSeen = conversation.lastMessageAt ? formatDistanceToNowStrict(new Date(conversation.lastMessageAt), { addSuffix: true }) : 'just now';

              return (
                <button
                  key={conversation._id}
                  type="button"
                  onClick={() => onSelectConversation?.(conversation)}
                  className={[
                    'w-full rounded-2xl border p-3 text-left transition',
                    isActive
                      ? 'border-cyan-400/40 bg-cyan-500/10 shadow-lg shadow-cyan-500/5'
                      : 'border-slate-200/80 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-white/10 dark:bg-slate-950/40 dark:hover:bg-white/5',
                  ].join(' ')}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-sm font-bold text-white dark:bg-white dark:text-slate-950">
                      {initials(conversation.displayName || conversation.userId || conversation.channelUserId)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="line-clamp-2 min-w-0 flex-1 break-all text-sm font-semibold leading-5 text-slate-900 dark:text-white" title={conversation.displayName || conversation.userId || conversation.channelUserId || 'Visitor'}>
                          {conversation.displayName || conversation.userId || conversation.channelUserId || 'Visitor'}
                        </div>
                        <span className={[
                          'inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium capitalize',
                          channelClass,
                        ].join(' ')}>
                          {conversation.channel}
                        </span>
                      </div>
                      <div className="mt-1 truncate text-sm text-slate-500 dark:text-slate-400">
                        {conversation.lastMessage || 'No messages yet'}
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <div className="text-xs text-slate-400 dark:text-slate-500">{lastSeen}</div>
                        <div className="flex items-center gap-2">
                          {conversation.unreadCount > 0 ? (
                            <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-cyan-500 px-2 py-0.5 text-[11px] font-bold text-white">
                              {conversation.unreadCount}
                            </span>
                          ) : null}
                          <span className={[
                            'rounded-full px-2 py-0.5 text-[11px] font-medium capitalize',
                          normalizeStatus(conversation.status) === 'open'
                              ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-200'
                              : normalizeStatus(conversation.status) === 'assigned'
                                ? 'bg-amber-500/10 text-amber-700 dark:text-amber-200'
                                : 'bg-slate-200 text-slate-600 dark:bg-white/10 dark:text-slate-300',
                          ].join(' ')}>
                            {statusMode === 'bot' && normalizeStatus(conversation.status) === 'open'
                              ? 'Active'
                              : normalizeStatus(conversation.status) === 'resolved'
                                ? 'Resolved'
                                : normalizeStatus(conversation.status)}
                          </span>
                        </div>
                      </div>
                      {conversationKind ? (
                        <div className="mt-2">
                          <span className={[
                            'inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                            conversationKind(conversation) === 'Bot chat'
                              ? 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-200'
                              : 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-200',
                          ].join(' ')}>
                            {conversationKind(conversation)}
                          </span>
                        </div>
                      ) : null}
                      {conversation.sla?.status && conversation.sla.status !== 'none' ? (
                        <div className="mt-2">
                          <span className={[
                            'inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                            conversation.sla.status === 'breached'
                              ? 'bg-rose-500/10 text-rose-700 dark:text-rose-200'
                              : conversation.sla.status === 'warning'
                                ? 'bg-amber-500/10 text-amber-700 dark:text-amber-200'
                                : 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-200',
                          ].join(' ')}>SLA {conversation.sla.status}</span>
                        </div>
                      ) : null}
                      {conversation.assignedTo ? (
                        <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2 py-0.5 text-[11px] font-medium text-violet-700 dark:text-violet-200">
                          Assigned to {assignedName(conversation.assignedTo) || 'agent'}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex h-full items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-400">
            No conversations found.
          </div>
        )}
      </div>

      {pagination.pages > pagination.page ? (
        <div className="pt-3">
          <button
            type="button"
            onClick={onLoadMore}
            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:border-cyan-300 hover:bg-cyan-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-cyan-500/10"
          >
            Load more
          </button>
        </div>
      ) : null}
    </aside>
  );
}
